import { useId, type MouseEventHandler } from 'react';
import kebabCase from 'lodash/kebabCase';
import { useLocale } from 'next-intl';
import cn from 'classnames';
import { Link } from 'src/navigation';
import SubNavItem from 'components/global/global-navigation/sub-nav-item';
import { ensureEngageLocale } from 'lib/engage';
import ExternalIcon from 'assets/icons/arrows/external.svg';
import Chevron from 'assets/icons/chevron-down.svg';
import styles from './nav-item.module.scss';

interface IProps {
	orientingCopy?: string;
	handleClick: MouseEventHandler<HTMLButtonElement>;
	isOpen: boolean;
	links?: { title: string; url: string }[];
	title: string;
	url?: string;
}

const NavItem = ({
	title,
	url,
	links,
	handleClick,
	isOpen,
	orientingCopy,
}: IProps) => {
	const subNavId = useId();
	const locale = useLocale();

	if (!url && !links?.length) {
		return null;
	}

	//For Nav Items without sub-items.
	if (url) {
		return (
			<li className={styles.navItem}>
				<Link
					target="_blank"
					rel="noopener noreferrer"
					prefetch={false}
					className={styles.externalLink}
					href={ensureEngageLocale(url, locale)}
					data-nav-id={title}
					data-analytics-id={`nav-item-${kebabCase(title)}-link`}
				>
					{title}
					<span className={styles.externalIcon} aria-hidden="true">
						<ExternalIcon />
					</span>
				</Link>
			</li>
		);
	}

	//For Nav items that are just top level groups
	if (links?.length) {
		return (
			<li
				className={cn(styles.navItem, { [styles.isOpen]: isOpen })}
				data-nav-item
			>
				<button
					type="button"
					className={styles.subNavToggle}
					onClick={handleClick}
					data-nav-id={title}
					aria-expanded={isOpen}
					aria-controls={subNavId}
					data-analytics-id={`nav-item-${kebabCase(title)}`}
				>
					{title}
					<span className={styles.icon} aria-hidden="true">
						<Chevron />
					</span>
				</button>
				{isOpen && (
					<div className={styles.subNav} id={subNavId}>
						<ul className={styles.subNavList}>
							{links.map((subItem) => (
								<SubNavItem
									parentText={title}
									key={subItem.title}
									url={subItem.url}
									text={subItem.title}
								/>
							))}
						</ul>
						<div className={styles.copy}>{orientingCopy}</div>
					</div>
				)}
			</li>
		);
	}
};

export default NavItem;
